import { ChevronLeftIcon } from "@chakra-ui/icons";
import { Box, HStack, Link } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  ContractStatusDto,
  type ContractReportQueryDto,
  type ProjectDto,
  useGetProjectQuery,
} from "../../../autogen/bff-api";
import { displayDate } from "../../../common/formatting/displayDate";
import { base64Encode } from "../../../common/utils/base64";
import { urls } from "../../../urls";
import { FilterChip, FilterChips } from "./OverviewFilterChips";

const FROM_PROJECT_PARAM = "project";

type FromProjectFilterKey = keyof Pick<ContractReportQueryDto, "projectIds" | "statuses" | "endDateTo">;

export const contractRegisterLinkFromProject = (projectId: string, q: Partial<ContractReportQueryDto>) =>
  `${urls.app.contracts.viewAll}?${new URLSearchParams({
    q: base64Encode(JSON.stringify(q)),
    [FROM_PROJECT_PARAM]: projectId,
  })}`;

export const fromProjectId = (search: string): string | null => new URLSearchParams(search).get(FROM_PROJECT_PARAM);

const sameIds = (a: string[], b: string[]) => a.length === b.length && b.every((id) => a.includes(id));

const projectScopeLabel = (t: TFunction, project: ProjectDto, projectIds: string[]): string | null => {
  const subprojectIds = project.children?.map((child) => child.id) ?? [];
  if (sameIds(projectIds, [project.id])) return t("Project: {{name}}", { name: project.name });
  if (subprojectIds.length === 0) return null;
  if (sameIds(projectIds, [project.id, ...subprojectIds]))
    return t("Project: {{name}}, with subprojects", { name: project.name });
  if (sameIds(projectIds, subprojectIds)) return t("Subprojects of {{name}}", { name: project.name });
  return null;
};

const isActiveEndingBefore = (query: ContractReportQueryDto) =>
  query.statuses?.length === 1 &&
  query.statuses[0] === ContractStatusDto.Active &&
  !query.endDateFrom &&
  !!query.endDateTo &&
  moment(query.endDateTo, "YYYY-MM-DD", true).isValid();

export const fromProjectChips = (
  t: TFunction,
  query: ContractReportQueryDto,
  project: ProjectDto,
): FilterChip<FromProjectFilterKey>[] => {
  const scope = projectScopeLabel(t, project, query.projectIds ?? []);
  return [
    ...(scope ? [{ label: scope, keys: ["projectIds" as const] }] : []),
    ...(scope && isActiveEndingBefore(query)
      ? [
          {
            label: t("Active, ending before {{date}}", {
              date: displayDate(moment(query.endDateTo, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD")),
            }),
            keys: ["statuses" as const, "endDateTo" as const],
          },
        ]
      : []),
  ];
};

export const FromProjectLine = ({
  projectId,
  query,
  onClear,
}: {
  projectId: string;
  query: ContractReportQueryDto;
  onClear: (keys: FromProjectFilterKey[]) => void;
}) => {
  const { t } = useTranslation();
  const { data: project } = useGetProjectQuery({ projectId });
  const loaded = project?.id === projectId ? project : undefined;

  return (
    <Box mb="3" data-testid="from-project">
      <Link
        as={RouterLink}
        to={urls.app.projects.view.go(projectId)}
        display="inline-block"
        mb="2"
        color="smPrimary"
        fontSize="sm"
        fontWeight="semibold"
        _hover={{ textDecoration: "none", color: "smPrimarySoft" }}
      >
        <HStack spacing="1">
          <ChevronLeftIcon boxSize="4" />
          <span>{loaded ? t("Back to «{{title}}»", { title: loaded.name }) : t("Back to the project")}</span>
        </HStack>
      </Link>
      {loaded && (
        <FilterChips chips={fromProjectChips(t, query, loaded)} onClear={onClear} testId="from-project-filter-chips" />
      )}
    </Box>
  );
};
